/**
 * Local-First Queues (HealthLoop wellness flows)
 * -----------------------------------------------------------------------------
 * Pre-configured queues for the lightweight wellness flows that are now
 * offline-safe (Daily Check-Ins + Reminders). Each queue knows how to ship
 * its own payload shape; everything else (state, retry, online detection)
 * lives in the generic createLocalFirstQueue factory.
 *
 * IMPORTANT: This file is the single allow-list for the local-first pattern.
 * Do NOT add prescriptions, clinical records, payments, or anything else
 * sensitive here. If you need to add a new queue, ask first — the MVP scope
 * is strictly low-risk wellness data.
 */

import { createLocalFirstQueue } from '@/services/localFirstQueue';
import { healthApi } from '@/api/healthApi';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------------------------------------------------------------------------
// 1) Daily Check-In queue
//
// Persists the full smart-check-in payload (mood, sleep, water, food, etc.)
// PLUS the matching daily-tracker task completions in one logical unit. The
// sync function fires the two backend writes in sequence; if either fails we
// throw so the queue re-parks the whole entry (no partial-success ambiguity).
//
// Dedupe key `date` ensures a user who submits twice on the same day
// only ever has one queued entry — the latest one wins.
// ---------------------------------------------------------------------------
export const checkinQueue = createLocalFirstQueue({
  name: 'checkins',
  allowDedupeKey: 'date',
  sync: async (payload) => {
    const { taskIds = [], smart = null } = payload || {};
    if (taskIds.length > 0) {
      await healthApi.markTasksComplete(taskIds);
    }
    if (smart) {
      await healthApi.createSmartCheckIn(smart);
    }
  },
});

// ---------------------------------------------------------------------------
// 2) Reminders queue
//
// Covers the CRUD operations users perform on their personal wellness
// reminders (light-touch nudges — water, walks, breathing breaks). We model
// every mutation as a single `op` field so the sync function can dispatch
// to the correct endpoint, and we preserve a `tempId` for create flows so
// the UI can swap optimistic IDs for real ones once the server confirms.
// ---------------------------------------------------------------------------
export const remindersQueue = createLocalFirstQueue({
  name: 'reminders',
  sync: async (payload) => {
    const { op } = payload || {};
    switch (op) {
      case 'create':
        await axios.post(`${API}/health-reminders`, payload.data, { withCredentials: true });
        return;
      case 'update':
        await axios.put(`${API}/health-reminders/${payload.id}`, payload.data, { withCredentials: true });
        return;
      case 'delete':
        await axios.delete(`${API}/health-reminders/${payload.id}`, { withCredentials: true });
        return;
      case 'complete':
        await axios.patch(`${API}/health-reminders/${payload.id}/complete`, {}, { withCredentials: true });
        return;
      default:
        throw new Error(`Unknown reminders op: ${op}`);
    }
  },
});
